import { createTheme, ThemeOptions, PaletteMode } from '@mui/material';
import { deDE as coreDeDE } from '@mui/material/locale';
import { deDE as dataGridDeDE } from '@mui/x-data-grid/locales';

// Die Farbe „ki“ als eigene Palettenfarbe — Buttons, IconButtons und Chips
// nehmen sie wie primary oder warning.
declare module '@mui/material/styles' {
  interface Palette {
    ki: Palette['primary'];
  }
  interface PaletteOptions {
    ki?: PaletteOptions['primary'];
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    ki: true;
  }
}
declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    ki: true;
  }
}
declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    ki: true;
  }
}

// revilion.de Design System
const revilion = {
  // Brand
  navy: '#1e3a5f',
  navyDark: '#162d4a',
  navyDeep: '#0f2137',
  accentBlue: '#3b6cb5',
  accentLight: '#5a8fd4',
  white: '#ffffff',
  // Light mode grays
  gray50: '#f7f8fa',
  gray100: '#eef0f4',
  gray200: '#dce1e8',
  gray300: '#b8c1ce',
  gray400: '#8e99a8',
  textPrimary: '#1a2b3c',
  textSecondary: '#5a6978',
  // Dark mode surfaces
  dark: {
    bg: '#0b1121',
    surface: '#111b2e',
    surfaceLight: '#162541',
    border: '#1e3354',
    borderLight: '#264060',
    textPrimary: '#e4e9f1',
    textSecondary: '#8899ad',
  },
  // Die Farbe der KI: Orange, sonst nirgends in der Oberfläche — damit jeder
  // Knopf, hinter dem ein Modell steht, auf den ersten Blick als solcher zu
  // erkennen ist. Ein Knopf, der aussieht wie jeder andere, wird gedrückt wie
  // jeder andere.
  ki: '#e8710a',
  kiLight: '#ff9800',
  kiDark: '#c25e00',
  // Status
  success: '#34a853',
  warning: '#f0ad4e',
  error: '#d93025',
  info: '#3b6cb5',
};

const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    primary: {
      main: revilion.navy,
      light: revilion.accentBlue,
      dark: revilion.navyDark,
      contrastText: revilion.white,
    },
    secondary: {
      main: revilion.accentBlue,
      light: revilion.accentLight,
      dark: revilion.navy,
      contrastText: revilion.white,
    },
    error: {
      main: revilion.error,
      light: '#e57373',
      dark: '#b71c1c',
    },
    warning: {
      main: revilion.warning,
      light: '#ffcc02',
      dark: '#c67c00',
    },
    success: {
      main: revilion.success,
      light: '#66bb6a',
      dark: '#1b5e20',
    },
    info: {
      main: revilion.info,
      light: revilion.accentLight,
      dark: revilion.navyDark,
    },
    ki: {
      main: mode === 'light' ? revilion.ki : revilion.kiLight,
      light: revilion.kiLight,
      dark: revilion.kiDark,
      contrastText: revilion.white,
    },
    background: {
      default: mode === 'light' ? revilion.gray50 : revilion.dark.bg,
      paper: mode === 'light' ? revilion.white : revilion.dark.surface,
    },
    text: {
      primary: mode === 'light' ? revilion.textPrimary : revilion.dark.textPrimary,
      secondary: mode === 'light' ? revilion.textSecondary : revilion.dark.textSecondary,
    },
    divider: mode === 'light' ? revilion.gray200 : revilion.dark.border,
  },
  typography: {
    fontFamily: '"DM Sans", "Inter", "Roboto", sans-serif',
    h1: { fontWeight: 700, fontSize: '2rem', lineHeight: 1.2, fontFamily: '"DM Sans", sans-serif' },
    h2: { fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.3, fontFamily: '"DM Sans", sans-serif' },
    h3: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.3, fontFamily: '"DM Sans", sans-serif' },
    h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4, fontFamily: '"DM Sans", sans-serif' },
    h5: { fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.4 },
    h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
    subtitle1: { fontWeight: 500, fontSize: '0.95rem' },
    subtitle2: { fontWeight: 500, fontSize: '0.875rem' },
    body1: { fontSize: '0.9rem', lineHeight: 1.6, fontFamily: '"Inter", sans-serif' },
    body2: { fontSize: '0.8125rem', lineHeight: 1.6, fontFamily: '"Inter", sans-serif' },
    button: { fontWeight: 600, textTransform: 'none' as const, fontFamily: '"Inter", sans-serif' },
    caption: { fontSize: '0.75rem', fontFamily: '"Inter", sans-serif' },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
          fontWeight: 500,
          fontSize: '0.8rem',
          letterSpacing: '0.01em',
          transition: 'all 0.2s ease',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: `0 4px 16px rgba(30, 58, 95, 0.25)`,
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: mode === 'light'
            ? '0 1px 3px rgba(0,0,0,0.06), 0 8px 30px rgba(0,0,0,0.04)'
            : '0 2px 8px rgba(0,0,0,0.4)',
          borderRadius: 10,
          border: mode === 'light'
            ? `1px solid ${revilion.gray200}`
            : `1px solid ${revilion.dark.border}`,
          backgroundColor: mode === 'light' ? revilion.white : revilion.dark.surfaceLight,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: mode === 'light'
            ? '0 1px 3px rgba(0,0,0,0.06), 0 8px 30px rgba(0,0,0,0.04)'
            : '0 2px 8px rgba(0,0,0,0.4)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? revilion.navy : '#0a1424',
          color: revilion.white,
          boxShadow: mode === 'light'
            ? '0 1px 3px rgba(0,0,0,0.12)'
            : '0 1px 3px rgba(0,0,0,0.5)',
          borderBottom: mode === 'dark' ? `1px solid ${revilion.dark.border}` : 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: mode === 'light' ? revilion.white : revilion.dark.surface,
          borderRight: `1px solid ${mode === 'light' ? revilion.gray100 : revilion.dark.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: mode === 'light'
              ? 'rgba(30, 58, 95, 0.08)'
              : 'rgba(59, 108, 181, 0.2)',
            color: mode === 'light' ? revilion.navy : revilion.accentLight,
            '&:hover': {
              backgroundColor: mode === 'light'
                ? 'rgba(30, 58, 95, 0.12)'
                : 'rgba(59, 108, 181, 0.28)',
            },
            '& .MuiListItemIcon-root': {
              color: mode === 'light' ? revilion.navy : revilion.accentLight,
            },
          },
          '&:hover': {
            backgroundColor: mode === 'light'
              ? revilion.gray50
              : 'rgba(59, 108, 181, 0.08)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: mode === 'light' ? revilion.gray50 : revilion.dark.surface,
            color: mode === 'light' ? revilion.textPrimary : revilion.dark.textPrimary,
            fontSize: '0.8rem',
            letterSpacing: '0.02em',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
          fontSize: '0.75rem',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          minHeight: 48,
          fontSize: '0.875rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: '0.75rem',
        },
      },
    },
  },
});

/**
 * Mit den deutschen Texten der Komponenten: Seitenwechsel, Bewertungen,
 * Auswahllisten und Tabellen sprachen sonst für Bildschirmleser englisch
 * („Go to previous page“, „column menu“; Testbericht TG 2.3, T45). Erst die
 * Kernkomponenten, dann die Tabelle — sie baut auf deren Seitenwechsel auf.
 */
export const createAppTheme = (mode: PaletteMode) => createTheme(getDesignTokens(mode), coreDeDE, dataGridDeDE);

export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');
